import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Piece {
  id: number;
  t: number; // top
  r: number; // right
  b: number; // bottom
  l: number; // left
}

interface Selected {
  piece: Piece;
  source: 'pool' | 'board';
  index: number; // posición en pool[] o board[]
}

interface HistoryEntry {
  board: (Piece | null)[];
  pool: Piece[];
}

// ─── Colores exactos de la versión PHP ───────────────────────────────────────

const NUM_COLORS: Record<number, string> = {
  0: '#FFFFFF', 1: '#FFCC00', 2: '#FF3333', 3: '#996633', 4: '#FFFF00',
  5: '#33CC33', 6: '#3399FF', 7: '#CC44FF', 8: '#999999', 9: '#333333',
};

const NUM_TEXT: Record<number, string> = {
  0: '#000', 1: '#000', 2: '#fff', 3: '#fff', 4: '#000',
  5: '#fff', 6: '#fff', 7: '#fff', 8: '#fff', 9: '#fff',
};

// ─── Componente SVG de pieza ─────────────────────────────────────────────────

function PieceSVG({ piece, size, selected = false, hint = false, hideNumbers = false, error = false }: {
  piece: Piece;
  size: string;
  selected?: boolean;
  hint?: boolean;
  hideNumbers?: boolean;
  error?: boolean;
}) {
  const triangles = [
    { points: '0,0 100,0 50,50',     value: piece.t, tx: 50, ty: 22 },
    { points: '100,0 100,100 50,50', value: piece.r, tx: 78, ty: 50 },
    { points: '0,100 100,100 50,50', value: piece.b, tx: 50, ty: 78 },
    { points: '0,0 0,100 50,50',     value: piece.l, tx: 22, ty: 50 },
  ];

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        overflow: 'hidden',
        boxShadow: selected
          ? '0 0 0 3px #3b82f6, 0 0 12px rgba(59,130,246,0.6)'
          : hint
          ? '0 0 0 3px #f97316, 0 0 12px rgba(249,115,22,0.6)'
          : error
          ? '0 0 0 3px #ef4444, 0 0 12px rgba(239,68,68,0.6)'
          : '0 1px 4px rgba(0,0,0,0.3)',
        transform: selected ? 'scale(1.08)' : 'scale(1)',
        transition: 'transform 0.1s, box-shadow 0.1s',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        {triangles.map((tri, i) => (
          <g key={i}>
            <polygon
              points={tri.points}
              fill={NUM_COLORS[tri.value]}
              stroke="#444"
              strokeWidth="1"
            />
            {!hideNumbers && (
              <text
                x={tri.tx}
                y={tri.ty}
                textAnchor="middle"
                dominantBaseline="middle"
                fill={NUM_TEXT[tri.value]}
                fontSize="28"
                fontWeight="bold"
                fontFamily="Segoe UI, Arial, sans-serif"
              >
                {tri.value}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

// ─── Dificultades ────────────────────────────────────────────────────────────

const DIFFICULTIES = [
  { orden: 1, nombre: 'Neurona',  icono: '🧠', size: 2 },
  { orden: 2, nombre: 'Sinapsis', icono: '⚡', size: 3 },
  { orden: 3, nombre: 'Cerebro',  icono: '🧩', size: 4 },
  { orden: 4, nombre: 'Genio',    icono: '💡', size: 5 },
  { orden: 5, nombre: 'Einstein', icono: '🎓', size: 6 },
];

// ─── Componente principal ─────────────────────────────────────────────────────

export default function TetravexGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();

  const dificultadOrden = Math.max(1, Math.min(5, parseInt(dificultad || '1')));
  const diff = DIFFICULTIES.find(d => d.orden === dificultadOrden) ?? DIFFICULTIES[0];
  const n = diff.size;

  // Estado del puzzle
  const [solution, setSolution] = useState<Piece[]>([]);
  const [board, setBoard] = useState<(Piece | null)[]>([]);
  const [pool, setPool] = useState<Piece[]>([]);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [moves, setMoves] = useState(0);
  const [hintIdx, setHintIdx] = useState<number | null>(null); // celda del tablero con pista activa

  // Estado de la UI
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [startTime, setStartTime] = useState(Date.now());
  const [seed, setSeed] = useState(0);
  const [boardLeft, setBoardLeft] = useState(true); // heminatención: invierte tablero ↔ piezas
  const [hideNumbers, setHideNumbers] = useState(false);
  const [locked, setLocked] = useState<Set<number>>(new Set());
  const [modoAsistido, setModoAsistido] = useState(false);

  const { saveProgress: saveGameProgress } = useGameProgress('tetravex', dificultadOrden);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Medición del área de juego
  const areaRef = useRef<HTMLDivElement>(null);
  const [areaW, setAreaW] = useState(0);
  const [areaH, setAreaH] = useState(0);

  const measureArea = () => {
    if (areaRef.current) {
      setAreaW(areaRef.current.clientWidth);
      setAreaH(areaRef.current.clientHeight);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureArea);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureArea);
    return () => window.removeEventListener('resize', measureArea);
  }, []);

  // ─── Cargar puzzle ─────────────────────────────────────────────────────────

  const loadPuzzle = useCallback(async () => {
    setLoading(true);
    setSelected(null);
    setIsCompleted(false);
    setShowVictory(false);
    setMoves(0);
    setHintIdx(null);
    setLocked(new Set());

    try {
      const res = await api.get('/tetravex/generate', { params: { size: n } });
      const data = res.data.data;

      setSolution(data.solution);
      setSeed(data.seed);

      const initBoard: (Piece | null)[] = new Array(n * n).fill(null);
      const initPool: Piece[] = [...data.pieces];

      setBoard(initBoard);
      setPool(initPool);
      setHistory([{ board: initBoard, pool: initPool }]);
      setHistIdx(0);
      setStartTime(Date.now());
    } catch {
      toast.error('Error al cargar el puzzle');
    } finally {
      setLoading(false);
    }
  }, [n]);

  useEffect(() => { loadPuzzle(); }, [loadPuzzle]);

  // ─── Historial ─────────────────────────────────────────────────────────────

  function commitMove(newBoard: (Piece | null)[], newPool: Piece[]) {
    const newHistory = history.slice(0, histIdx + 1);
    newHistory.push({ board: [...newBoard], pool: [...newPool] });
    setHistory(newHistory);
    setHistIdx(newHistory.length - 1);
    setBoard(newBoard);
    setPool(newPool);
    setMoves(m => m + 1);
    setHintIdx(null);
    checkWin(newBoard);
  }

  function deshacer() {
    if (histIdx <= 0 || isCompleted) return;
    const prev = history[histIdx - 1];
    setHistIdx(histIdx - 1);
    setBoard([...prev.board]);
    setPool([...prev.pool]);
    setSelected(null);
    setHintIdx(null);
  }

  function rehacer() {
    if (histIdx >= history.length - 1 || isCompleted) return;
    const next = history[histIdx + 1];
    setHistIdx(histIdx + 1);
    setBoard([...next.board]);
    setPool([...next.pool]);
    setSelected(null);
    setHintIdx(null);
  }

  // ─── Comprobación de victoria ──────────────────────────────────────────────

  function checkWin(b: (Piece | null)[]) {
    // 1. Todos los huecos rellenos
    if (b.some(p => p === null)) return;

    // 2. Todos los bordes internos coinciden
    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const p = b[row * n + col]!;
        if (col < n - 1 && p.r !== b[row * n + col + 1]!.l) return;
        if (row < n - 1 && p.b !== b[(row + 1) * n + col]!.t) return;
      }
    }

    if (!isCompleted) {
      setIsCompleted(true);
      const tiempo = Math.floor((Date.now() - startTime) / 1000);
      saveProgress(tiempo);
      setShowVictory(true);
    }
  }

  async function saveProgress(tiempo: number) {
    try {
      await saveGameProgress({ tablero_id: seed, tiempo, movimientos: moves });
      await saveSessionResult({
        tiempo,
        movimientos: moves,
        completado: true,
        datos_juego: { tablero_id: seed, size: n },
      });
    } catch { /* silent */ }
  }

  // ─── Interacción: click en pieza (pool o tablero) ─────────────────────────

  function handlePieceClick(piece: Piece, source: 'pool' | 'board', index: number) {
    if (isCompleted) return;

    if (source === 'board') {
      // Ya seleccionada → devolver al pool
      if (selected?.source === 'board' && selected.index === index) {
        const newBoard = [...board];
        const newPool = [piece, ...pool];
        newBoard[index] = null;
        commitMove(newBoard, newPool);
        setSelected(null);
        return;
      }
      if (locked.has(index)) {
        // Bloqueada → desbloquear y seleccionar
        setLocked(prev => { const next = new Set(prev); next.delete(index); return next; });
        setSelected({ piece, source, index });
      } else {
        // Sin bloquear → bloquear (no seleccionar)
        setLocked(prev => new Set(prev).add(index));
        setSelected(null);
      }
      return;
    }

    // Pieza del pool → seleccionar normalmente
    if (!selected) {
      setSelected({ piece, source, index });
      return;
    }

    // Misma pieza → deseleccionar (y si está en el tablero, devolverla al pool)
    if (selected.source === source && selected.index === index) {
      if (source === 'board') {
        const newBoard = [...board];
        const newPool = [piece, ...pool];
        newBoard[index] = null;
        commitMove(newBoard, newPool);
      }
      setSelected(null);
      return;
    }

    // Distinta pieza del pool → cambiar selección
    if (source === 'pool') {
      setSelected({ piece, source, index });
      return;
    }

    // Distinta pieza del tablero → intercambiar
    if (locked.has(index)) { setSelected(null); return; } // destino bloqueado

    const newBoard = [...board];
    const newPool = [...pool];

    if (selected.source === 'pool') {
      // pool → celda ocupada del tablero: intercambio
      newBoard[index] = selected.piece;
      newPool[selected.index] = piece; // la desplazada vuelve al hueco del pool
    } else {
      // tablero → tablero: swap directo
      newBoard[index] = selected.piece;
      newBoard[selected.index] = piece;
    }

    commitMove(newBoard, newPool);
    setSelected(null);
  }

  // ─── Interacción: click en celda vacía ────────────────────────────────────

  function handleCellClick(cellIndex: number) {
    if (isCompleted || !selected || board[cellIndex] !== null) return;

    const newBoard = [...board];
    const newPool = [...pool];

    if (selected.source === 'pool') {
      newBoard[cellIndex] = selected.piece;
      newPool.splice(selected.index, 1);
    } else {
      newBoard[cellIndex] = selected.piece;
      newBoard[selected.index] = null;
    }

    commitMove(newBoard, newPool);
    setSelected(null);
  }

  // ─── Pista ────────────────────────────────────────────────────────────────

  function darPista() {
    if (isCompleted) return;

    // Encontrar primera celda incorrecta o vacía
    let targetIdx = -1;
    for (let i = 0; i < n * n; i++) {
      if (board[i]?.id !== solution[i].id) { targetIdx = i; break; }
    }
    if (targetIdx === -1) return;

    const correctPiece = solution[targetIdx];
    const newBoard = [...board];
    const newPool = [...pool];

    // ¿Dónde está la pieza correcta ahora?
    const poolIdx = newPool.findIndex(p => p.id === correctPiece.id);
    const boardIdx = newBoard.findIndex(p => p?.id === correctPiece.id);

    if (poolIdx !== -1) {
      // La pieza está en el pool
      if (newBoard[targetIdx] !== null) {
        // Desplazar la que ya había allí al pool
        newPool.push(newBoard[targetIdx]!);
      }
      newBoard[targetIdx] = correctPiece;
      newPool.splice(poolIdx, 1);
    } else if (boardIdx !== -1 && boardIdx !== targetIdx) {
      // La pieza está en otra celda del tablero → swap
      const displaced = newBoard[targetIdx];
      newBoard[targetIdx] = correctPiece;
      newBoard[boardIdx] = displaced;
    }

    commitMove(newBoard, newPool);
    setSelected(null);
    setHintIdx(targetIdx);
    setLocked(prev => new Set(prev).add(targetIdx));
    setTimeout(() => setHintIdx(null), 1600);
  }

  // ─── Ver solución ─────────────────────────────────────────────────────────

  function verSolucion() {
    if (!window.confirm('¿Mostrar la solución? El progreso no se guardará.')) return;
    const solBoard = [...solution];
    commitMove(solBoard, []);
    setSelected(null);
  }

  // ─── Reiniciar ────────────────────────────────────────────────────────────

  function reiniciar() {
    if (!window.confirm('¿Reiniciar el tablero?')) return;
    const first = history[0];
    if (!first) return;
    setBoard([...first.board]);
    setPool([...first.pool]);
    setHistory([first]);
    setHistIdx(0);
    setMoves(0);
    setSelected(null);
    setHintIdx(null);
    setIsCompleted(false);
    setShowVictory(false);
    setStartTime(Date.now());
    setLocked(new Set());
  }

  // ─── Modo asistido ────────────────────────────────────────────────────────

  function calcularErrores(b: (Piece | null)[], sz: number): Set<number> {
    const errores = new Set<number>();
    for (let row = 0; row < sz; row++) {
      for (let col = 0; col < sz; col++) {
        const idx = row * sz + col;
        const p = b[idx];
        if (!p) continue;
        // derecha
        if (col < sz - 1) {
          const right = b[idx + 1];
          if (right && p.r !== right.l) { errores.add(idx); errores.add(idx + 1); }
        }
        // abajo
        if (row < sz - 1) {
          const below = b[(row + 1) * sz + col];
          if (below && p.b !== below.t) { errores.add(idx); errores.add((row + 1) * sz + col); }
        }
      }
    }
    return errores;
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Generando puzzle…</div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-indigo-600 to-purple-700 flex flex-col"
      onClick={() => setSelected(null)} // clic fuera → deseleccionar
    >
      {isInSession && sessionType && (
        <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />
      )}

      {/* ── Barra de controles ── */}
      <div
        className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap"
        onClick={e => e.stopPropagation()}
      >
        <span className="text-white font-bold text-base whitespace-nowrap">🟦 Tetravex</span>

        <div className="w-px h-5 bg-white/30 hidden sm:block" />

        {/* Selector de dificultad */}
        <div className="flex gap-1 flex-wrap">
          {DIFFICULTIES.map(d => (
            <button
              key={d.orden}
              onClick={() => navigate(`/juegos/tetravex/${d.orden}`)}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dificultadOrden === d.orden
                  ? 'bg-white text-indigo-700 shadow'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              title={`${d.nombre} — ${d.size}×${d.size}`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Acciones */}
        <div className="flex gap-1 items-center" onClick={e => e.stopPropagation()}>
          {/* Deshacer */}
          <button onClick={deshacer} disabled={histIdx <= 0 || isCompleted}
            title="Deshacer"
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M3 7v6h6"/><path d="M3 13C5 8 9.5 5 15 5c5 0 8 3 8 7s-3 7-8 7H9"/>
            </svg>
          </button>

          {/* Rehacer */}
          <button onClick={rehacer} disabled={histIdx >= history.length - 1 || isCompleted}
            title="Rehacer"
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 7v6h-6"/><path d="M21 13C19 8 14.5 5 9 5c-5 0-8 3-8 7s3 7 8 7h6"/>
            </svg>
          </button>

          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-2 rounded-lg transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>

          {/* Pista */}
          <button onClick={darPista} disabled={isCompleted}
            title="Pista"
            className="p-2 rounded-lg bg-orange-500/70 text-white hover:bg-orange-500 disabled:opacity-30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
            </svg>
          </button>

          {/* Ver solución */}
          <button onClick={verSolucion} disabled={isCompleted}
            title="Ver solución"
            className="p-2 rounded-lg bg-red-500/70 text-white hover:bg-red-500 disabled:opacity-30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </button>

          {/* Reiniciar */}
          <button onClick={reiniciar} disabled={isCompleted}
            title="Reiniciar"
            className="p-2 rounded-lg bg-red-500/70 text-white hover:bg-red-500 disabled:opacity-30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>
            </svg>
          </button>

          {/* Nuevo tablero */}
          <button onClick={loadPuzzle}
            title="Nuevo tablero"
            className="p-2 rounded-lg bg-green-500/70 text-white hover:bg-green-500 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </button>

          {/* Invertir layout (heminatención) */}
          <button onClick={() => setBoardLeft(v => !v)}
            title={boardLeft ? 'Mover tablero a la derecha (heminatención izquierda)' : 'Mover tablero a la izquierda (heminatención derecha)'}
            className={`p-2 rounded-lg text-white transition-all ${boardLeft ? 'bg-white/20 hover:bg-white/30' : 'bg-violet-500 hover:bg-violet-600'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M4 9l-3 3 3 3"/><path d="M1 12h10"/><path d="M20 15l3-3-3-3"/><path d="M23 12H13"/>
            </svg>
          </button>

          {/* Ocultar números */}
          <button onClick={() => setHideNumbers(v => !v)}
            title={hideNumbers ? 'Mostrar números' : 'Ocultar números (solo colores)'}
            className={`p-2 rounded-lg text-white transition-all ${hideNumbers ? 'bg-violet-500 hover:bg-violet-600' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <text x="4" y="17" fontSize="14" fontWeight="bold" stroke="currentColor" fill="currentColor">9</text>
              <line x1="2" y1="22" x2="22" y2="2"/>
            </svg>
          </button>

          {/* Ayuda */}
          <button onClick={() => setShowHowToPlay(true)}
            title="Cómo se juega"
            className="p-2 rounded-lg bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Info bar ── */}
      <div className="bg-black/10 px-3 py-1 flex items-center gap-4 text-white/80 text-xs"
        onClick={e => e.stopPropagation()}>
        <span>{diff.icono} {diff.nombre} — {n}×{n}</span>
        <span>🎯 {moves} mov</span>
        <span className="text-white/50">
          {pool.length > 0 ? `${pool.length} piezas restantes` : '¡Tablero completo!'}
        </span>
      </div>

      {/* ── Área principal: tablero + pool (orden invertible para heminatención) ── */}
      <div
        ref={areaRef}
        className={`flex-1 min-h-0 flex ${boardLeft ? 'flex-row' : 'flex-row-reverse'} items-stretch gap-2 p-2 overflow-hidden`}
        onClick={e => e.stopPropagation()}
      >
        {areaW > 0 && areaH > 0 && (() => {
          const errores = modoAsistido ? calcularErrores(board, n) : new Set<number>();
          const gap = 4;
          const pad = 4;
          // Board gets ~58% of width (rest for pool), constrained by height
          const boardAreaW = Math.floor(areaW * 0.58);
          const cellFromW = Math.floor((boardAreaW - pad * 2 - gap * (n - 1)) / n);
          const cellFromH = Math.floor((areaH - pad * 2 - gap * (n - 1)) / n);
          const cs = Math.min(cellFromW, cellFromH); // board cell size in px
          const boardPx = cs * n + gap * (n - 1) + pad * 2;
          // Pool pieces slightly smaller so more fit
          const poolCs = Math.max(32, Math.floor(cs * 0.82));

          return (
            <>
              {/* Tablero */}
              <div style={{ width: boardPx, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${n}, ${cs}px)`,
                  gridTemplateRows: `repeat(${n}, ${cs}px)`,
                  gap,
                  padding: pad,
                }}>
                  {board.map((piece, idx) => {
                    const isSelected = selected?.source === 'board' && selected.index === idx;
                    const isHint = hintIdx === idx;
                    return (
                      <div
                        key={idx}
                        onClick={e => {
                          e.stopPropagation();
                          piece ? handlePieceClick(piece, 'board', idx) : handleCellClick(idx);
                        }}
                        style={{ width: cs, height: cs }}
                        className={`flex items-center justify-center rounded-lg transition-all ${
                          piece
                            ? 'cursor-pointer'
                            : selected
                            ? 'cursor-pointer border-2 border-dashed border-blue-300/60 hover:border-blue-300 bg-white/5 hover:bg-white/10'
                            : 'border-2 border-dashed border-white/20 bg-white/5'
                        }`}
                      >
                        {piece && (
                          <div style={{ position: 'relative' }}>
                            <PieceSVG piece={piece} size={`${cs}px`} selected={isSelected} hint={isHint} hideNumbers={hideNumbers} error={!isSelected && !isHint && errores.has(idx)} />
                            {locked.has(idx) && (
                              <div style={{
                                position: 'absolute', top: 2, right: 2,
                                fontSize: Math.max(10, Math.floor(cs * 0.22)),
                                lineHeight: 1, pointerEvents: 'none',
                              }}>🔒</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pool de piezas */}
              {pool.length > 0 && (
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                  onClick={e => e.stopPropagation()}>
                  <p className="text-white/50 text-xs text-center mb-1 uppercase tracking-widest">
                    Piezas disponibles
                  </p>
                  <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', alignContent: 'flex-start', padding: 4 }}>
                    {pool.map((piece, idx) => {
                      const isSelected = selected?.source === 'pool' && selected.index === idx;
                      return (
                        <div key={piece.id} onClick={e => { e.stopPropagation(); handlePieceClick(piece, 'pool', idx); }}>
                          <PieceSVG piece={piece} size={`${poolCs}px`} selected={isSelected} hideNumbers={hideNumbers} />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* ── Modales ── */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Tetravex"
        gameIcon="🟦"
        objective="Coloca todas las piezas en el tablero de forma que los números de los bordes que se tocan sean iguales."
        rules={[
          'Cada pieza tiene 4 números: arriba, abajo, izquierda y derecha',
          'Dos piezas adyacentes deben tener el mismo número en el borde que comparten',
          'El borde derecho de una pieza debe coincidir con el borde izquierdo de la pieza de su derecha',
          'El borde inferior de una pieza debe coincidir con el borde superior de la pieza de abajo',
        ]}
        howToPlay="Toca una pieza para seleccionarla (se resalta en azul). Luego toca una celda vacía del tablero para colocarla. Toca otra pieza para intercambiarlas. Toca la pieza seleccionada del tablero para devolverla al pool."
        author="Scott Ferguson (Windows Entertainment Pack)"
      />

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{
          tiempo: Math.floor((Date.now() - startTime) / 1000),
          movimientos: moves,
        }}
      />
    </div>
  );
}
